import type { InboxPage } from '@/services/conversations/inbox';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import type { components } from '@/types/api';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import {
  compareSentOrder,
  getQuestionGroupKey,
  type NotSentExpert,
  type QuestionGroup,
  type QuestionGroupSplit,
} from '@/util/questionGroups';

export type ServerInboxQuestionGroup = components['schemas']['InboxQuestionGroup'];

export type ServerInboxEntry = components['schemas']['InboxEntry'];

/** One entry of a page requested with `groupBy=question`. */
export type GroupedInboxEntry =
  | components['schemas']['InboxQuestionGroupEntry']
  | components['schemas']['InboxSingleEntry'];

/**
 * What a grouped page adds to the inbox page the tabs have always held. `questionGroups` are the question
 * groups the server built, already in the card's own shape. `questionGroupMemberIds` names every
 * conversation an inquiry group owns, so a row that is in `inquiries` but not in an inquiry group is a single.
 */
export type GroupedPageParts = {
  inquiries: InquiryV2[];
  order: string[];
  questionGroups: QuestionGroup[];
  questionGroupMemberIds: string[];
  singles: ServerInboxEntry[];
};

/**
 * An old server ignores `groupBy` and answers with the flat page, whose entries carry no `type`.
 * An empty page counts as grouped: it reads the same either way.
 */
export function isGroupedEntries(
  entries: readonly (ServerInboxEntry | GroupedInboxEntry)[]
): entries is GroupedInboxEntry[] {
  return entries.every((entry) => 'type' in entry);
}

/** The server types the inquiry items like the flat list's; the app's own type is the one the card reads. */
const asInquiry = (inquiry: ServerInboxQuestionGroup['originals'][number]) =>
  inquiry as unknown as InquiryV2;

function withoutOutsideMarker(original: NonNullable<ServerInboxQuestionGroup['original']>) {
  const { fromOutsideInbox: _marker, ...inquiry } = original;

  return inquiry;
}

/**
 * Turns a server inquiry group into the inquiry group the card already renders, so the card needs no change.
 *
 * - The title thread is the server's `original`, then the first of `originals`, then the newest
 *   member, so an inquiry group whose submitter thread cannot be read still has a title. An original read
 *   from outside the viewer's inbox is marked fetched: it never decides where the inquiry group sits.
 * - `threads` are the per-expert threads in the server's first-sent order. A member that is
 *   neither a submitter thread nor an expert's current thread, such as an older re-forward, follows
 *   them oldest first, so no thread the viewer is in goes missing from the list.
 * - `notSentExperts` are the picked experts nobody has relayed to yet. Each carries the submitter's
 *   fan-out thread that holds the pick, so the card can open it. The key is the original's
 *   conversation id, the one client grouping uses, and the server's entry key only when there is no
 *   original to name it.
 *
 * Returns `null` for an inquiry group that has no thread at all, which the server does not send.
 */
export function toQuestionGroup(
  entryKey: string,
  serverQuestionGroup: ServerInboxQuestionGroup
): QuestionGroup | null {
  const { original: serverOriginal, originals, experts, members } = serverQuestionGroup;

  // The generated type cannot say null (see the schema's allOf), but the server sends it.
  const fromOutsideInbox = serverOriginal?.fromOutsideInbox === true;

  const titleThread = serverOriginal
    ? withoutOutsideMarker(serverOriginal)
    : (originals[0] ?? members[0]);

  if (!titleThread) {
    return null;
  }

  const original = asInquiry(titleThread);

  // The title is a submitter thread only when it is one of the viewer's own originals. An original
  // read from outside the inbox only titles the card, and a relay standing in for an original
  // nobody could read stays one of the card's rows.
  const titleIsSubmitterThread = serverOriginal ? !fromOutsideInbox : originals.length > 0;

  const submitterThreads = [
    ...(titleIsSubmitterThread ? [original] : []),
    ...originals
      .map(asInquiry)
      .filter((inquiry) => inquiry.conversationId !== original.conversationId),
  ];

  const submitterIds = new Set(submitterThreads.map((inquiry) => inquiry.conversationId));

  const memberById = new Map(members.map((member) => [member.conversationId, asInquiry(member)]));

  const notSentExperts = experts.flatMap((expert): NotSentExpert[] => {
    const thread = expert.conversationId
      ? (memberById.get(expert.conversationId) ??
        submitterThreads.find((inquiry) => inquiry.conversationId === expert.conversationId))
      : undefined;

    if (expert.state !== 'not_sent' || !thread) {
      return [];
    }

    return [
      {
        id: expert.expert.id,
        name: getProfileNameAndInitials({
          firstName: expert.expert.firstName,
          lastName: expert.expert.lastName,
          identifiesAsDoctor: expert.expert.identifiesAsDoctor,
        }).name,
        lastName: expert.expert.lastName?.trim() || expert.expert.firstName?.trim() || '',
        thread,
      },
    ];
  });

  const submitterSide = serverQuestionGroup.side === 'submitter';

  // On the submitter side each row is one of the submitter's own threads, so it stays a row.
  const expertThreads = experts.flatMap((expert) => {
    const thread =
      expert.state !== 'not_sent' && expert.conversationId
        ? memberById.get(expert.conversationId)
        : undefined;

    return thread && (submitterSide || !submitterIds.has(thread.conversationId)) ? [thread] : [];
  });

  const placedIds = new Set([
    ...submitterIds,
    ...notSentExperts.map((expert) => expert.thread.conversationId),
    ...expertThreads.map((thread) => thread.conversationId),
  ]);

  const otherThreads = [...memberById.values()]
    .filter((member) => !placedIds.has(member.conversationId))
    .sort(compareSentOrder);

  const baseKey = getQuestionGroupKey(
    serverOriginal?.conversationId ?? originals[0]?.conversationId ?? entryKey
  );

  return {
    // Both sides of a question are titled from the same original, so the relay side keys apart.
    key: serverQuestionGroup.side === 'relay' ? `${baseKey}:relay` : baseKey,
    original,
    originalFetched: fromOutsideInbox,
    // A relay-side card's original sits on the other card; here it only titles this one.
    originalGroup: submitterThreads,
    ...(serverQuestionGroup.side ? { side: serverQuestionGroup.side } : {}),
    threads: [...expertThreads, ...otherThreads],
    notSentExperts,
  };
}

/**
 * Splits a grouped page into the parts an inbox page holds.
 *
 * `order` is the server's order across questions and singles. An inquiry group takes the place of the
 * threads it owns, all listed at the inquiry group's position, so the list puts the card where the server
 * did. `inquiries` holds every thread of the page, single or in an inquiry group, once each: the cache
 * patches written against the flat list find their row there, and the card is refreshed from it.
 */
export function readGroupedEntries(entries: readonly GroupedInboxEntry[]): GroupedPageParts {
  const inquiriesById = new Map<string, InquiryV2>();

  const order = new Set<string>();

  const questionGroups: QuestionGroup[] = [];

  const questionGroupMemberIds = new Set<string>();

  const singles: ServerInboxEntry[] = [];

  const keep = (inquiry: InquiryV2) => {
    if (!inquiriesById.has(inquiry.conversationId)) {
      inquiriesById.set(inquiry.conversationId, inquiry);
    }

    order.add(inquiry.conversationId);
  };

  entries.forEach((entry) => {
    if (entry.type === 'single') {
      singles.push(entry.entry);

      order.add(entry.entry.conversationId);

      if (entry.entry.inquiry) {
        keep(asInquiry(entry.entry.inquiry));
      }

      return;
    }

    const questionGroup = toQuestionGroup(entry.key, entry.group);

    if (!questionGroup) {
      return;
    }

    questionGroups.push(questionGroup);

    [
      ...questionGroup.originalGroup,
      ...questionGroup.threads,
      ...(questionGroup.notSentExperts ?? []).map((expert) => expert.thread),
    ]
      .filter((inquiry) => !(inquiry === questionGroup.original && questionGroup.originalFetched))
      .forEach((inquiry) => {
        keep(inquiry);

        questionGroupMemberIds.add(inquiry.conversationId);
      });
  });

  return {
    inquiries: [...inquiriesById.values()],
    order: [...order],
    questionGroups,
    questionGroupMemberIds: [...questionGroupMemberIds],
    singles,
  };
}

/**
 * Brings an inquiry group's threads up to date with the rows the cache has since patched (unread, status,
 * preview). A thread the cache no longer holds keeps the copy the inquiry group came with.
 */
export function refreshQuestionGroups(
  questionGroups: readonly QuestionGroup[],
  loaded: readonly InquiryV2[]
): QuestionGroup[] {
  const loadedById = new Map(loaded.map((inquiry) => [inquiry.conversationId, inquiry]));

  const latest = (inquiry: InquiryV2) => loadedById.get(inquiry.conversationId) ?? inquiry;

  return questionGroups.map((questionGroup) => ({
    ...questionGroup,
    original: latest(questionGroup.original),
    originalGroup: questionGroup.originalGroup.map(latest),
    threads: questionGroup.threads.map(latest),
    notSentExperts: questionGroup.notSentExperts?.map((expert) => ({
      ...expert,
      thread: latest(expert.thread),
    })),
  }));
}

/**
 * The inquiry groups and the rest for a tab whose pages the server grouped, or `null` for a flat tab, so
 * the caller falls back to grouping on the client. Every page must be grouped: one flat page among
 * them means the server stopped grouping mid-list and the client's own grouping is the safe answer.
 *
 * `inquiries` is what the tab shows after its own filters. Whatever an inquiry group owns is taken out of it,
 * and the rest, including a row a cache patch has added since the page loaded, stays a flat row.
 */
export function splitServerQuestionGroups(
  pages: readonly (InboxPage | undefined)[] | undefined,
  inquiries: readonly InquiryV2[]
): QuestionGroupSplit | null {
  if (!pages?.length) {
    return null;
  }

  const groupedPages = pages.flatMap((page) => (page?.questionGroups ? [page] : []));

  if (groupedPages.length !== pages.length) {
    return null;
  }

  const loaded = groupedPages.flatMap((page) => page.inquiries) as unknown as InquiryV2[];

  const owned = new Set(groupedPages.flatMap((page) => page.questionGroupMemberIds ?? []));

  return {
    questionGroups: refreshQuestionGroups(
      groupedPages.flatMap((page) => page.questionGroups ?? []),
      loaded
    ),
    remaining: inquiries.filter((inquiry) => !owned.has(inquiry.conversationId)),
  };
}
